import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Switch } from '../ui/switch'
import { Label } from '../ui/label'
import { CheckIcon, Loader2Icon } from 'lucide-react'

interface Organization {
  current_plan: string
}

interface Subscription {
  status: string
  plan_name: string
  stripe_price_id: string
}

interface PlanVariation {
  stripe_price_id: string
  credits: number
  price: number
  monthly_price: number
  display_name: string
  billing_interval: 'month' | 'year'
}

interface TierVariations {
  month: PlanVariation[]
  year: PlanVariation[]
}

type PlanVariations = Record<string, {
  name: string
  description: string
  variations: TierVariations
}>

interface SubscriptionData {
  organization: Organization
  subscription: Subscription | null
  plan_variations: PlanVariations
}

interface UpgradePlanModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UpgradePlanModal({ open, onOpenChange }: UpgradePlanModalProps) {
  const [data, setData] = useState<SubscriptionData | null>(null)
  const [fetchError, setFetchError] = useState(false)

  // State for selected variations per tier
  const [selectedVariations, setSelectedVariations] = useState<Record<string, string | undefined>>({})

  // State for billing interval toggle
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month')

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingTierChange, setPendingTierChange] = useState<string | null>(null)

  // Helper to get variations for current billing interval
  const getVariationsForInterval = (tier: string) => {
    if (!data) return []
    return data.plan_variations[tier]?.variations?.[billingInterval] || []
  }

  // Fetch subscription data when modal opens
  useEffect(() => {
    if (open && !data) {
      fetch('/organization/subscription_data', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.json()
        })
        .then((responseData: SubscriptionData) => {
          setData(responseData)

          // Initialize selected variations using month by default
          const tiers = Object.keys(responseData.plan_variations)
          const initial: Record<string, string | undefined> = {}
          for (const tier of tiers) {
            initial[tier] = responseData.plan_variations[tier].variations.month[0]?.stripe_price_id
          }

          // If user has active subscription, set that tier's variation to their current price
          if (responseData.subscription && responseData.subscription.status === 'active' && responseData.subscription.stripe_price_id) {
            initial[responseData.subscription.plan_name] = responseData.subscription.stripe_price_id
          }

          setSelectedVariations(initial)

          // If user has active subscription, detect and set their billing interval
          if (responseData.subscription?.status === 'active' && responseData.subscription?.stripe_price_id) {
            const currentPriceId = responseData.subscription.stripe_price_id
            const currentTierKey = responseData.subscription.plan_name

            // Search all tiers and intervals to find the matching variation
            outerLoop:
            for (const tier of tiers) {
              for (const interval of ['month', 'year'] as const) {
                const variations = responseData.plan_variations[tier]?.variations?.[interval] || []
                const match = variations.find(v => v.stripe_price_id === currentPriceId)
                if (match) {
                  setBillingInterval(interval)

                  // Update non-current tiers to use this interval's variations
                  for (const otherTier of tiers) {
                    if (otherTier !== currentTierKey) {
                      initial[otherTier] = responseData.plan_variations[otherTier].variations[interval][0]?.stripe_price_id
                    }
                  }
                  setSelectedVariations(initial)

                  break outerLoop
                }
              }
            }
          }
        })
        .catch(error => {
          console.error('Failed to fetch subscription data:', error)
          setFetchError(true)
        })
    }
  }, [open, data])

  const handleVariationChange = (tier: string, priceId: string) => {
    setSelectedVariations(prev => ({
      ...prev,
      [tier]: priceId
    }))
  }

  // Update selected variations when billing interval changes
  const handleBillingIntervalChange = (newInterval: 'month' | 'year') => {
    if (!data) return
    setBillingInterval(newInterval)

    // Update selected variations to match the same credit level in the new interval
    const newSelections: Record<string, string | undefined> = {}

    for (const tier of Object.keys(data.plan_variations)) {
      const currentPriceId = selectedVariations[tier]
      const oldVariations = data.plan_variations[tier].variations[billingInterval]
      const newVariations = data.plan_variations[tier].variations[newInterval]

      // Find the current selection's credits amount
      const currentVariation = oldVariations?.find(v => v.stripe_price_id === currentPriceId)
      const creditsToMatch = currentVariation?.credits

      if (creditsToMatch && newVariations) {
        // Find matching credits in new interval
        const matchingVariation = newVariations.find(v => v.credits === creditsToMatch)
        newSelections[tier] = matchingVariation?.stripe_price_id || newVariations[0]?.stripe_price_id
      } else {
        newSelections[tier] = newVariations?.[0]?.stripe_price_id
      }
    }

    setSelectedVariations(newSelections)
  }

  const getSelectedVariation = (tier: string) => {
    if (!data) return null
    const variations = getVariationsForInterval(tier)
    const selectedPriceId = selectedVariations[tier]
    return variations.find(v => v.stripe_price_id === selectedPriceId)
  }

  const handleSubscribe = (tier: string) => {
    const stripePriceId = selectedVariations[tier]
    if (!stripePriceId) return

    // Use a regular form submission to allow external redirects (Stripe Checkout)
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/billing/checkout'

    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'stripe_price_id'
    input.value = stripePriceId
    form.appendChild(input)

    // Add return_to parameter to redirect back to current page after checkout
    const returnToInput = document.createElement('input')
    returnToInput.type = 'hidden'
    returnToInput.name = 'return_to'
    returnToInput.value = window.location.pathname
    form.appendChild(returnToInput)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const handleChangePlan = (tier: string) => {
    const stripePriceId = selectedVariations[tier]
    if (!stripePriceId) return

    // Show confirmation dialog
    setPendingTierChange(tier)
    setShowConfirmDialog(true)
  }

  const confirmChangePlan = () => {
    if (!pendingTierChange) return

    const stripePriceId = selectedVariations[pendingTierChange]
    if (!stripePriceId) return

    // Use regular form submission
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/billing/subscription/update'

    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'stripe_price_id'
    input.value = stripePriceId
    form.appendChild(input)

    // Add return_to parameter to redirect back to current page
    const returnToInput = document.createElement('input')
    returnToInput.type = 'hidden'
    returnToInput.name = 'return_to'
    returnToInput.value = window.location.pathname
    form.appendChild(returnToInput)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()

    // Reset dialog state
    setShowConfirmDialog(false)
    setPendingTierChange(null)
  }

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{fetchError ? 'Plans Unavailable' : 'Loading Plans...'}</DialogTitle>
          </DialogHeader>
          {fetchError ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-sm text-muted-foreground">Unable to load plan information. Please try again later.</p>
              <Button variant="outline" className="mt-4" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    )
  }

  const isFreePlan = data.organization.current_plan === 'free'
  const hasActiveSubscription = data.subscription && data.subscription.status === 'active'

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              {isFreePlan ? 'Upgrade Your Plan' : 'Change Your Plan'}
            </DialogTitle>
          </DialogHeader>

          {/* Billing Interval Toggle */}
          <div className="flex items-center justify-center gap-3 py-2">
            <Label htmlFor="billing-toggle" className={billingInterval === 'month' ? 'font-medium' : 'text-muted-foreground'}>
              Monthly
            </Label>
            <Switch
              id="billing-toggle"
              checked={billingInterval === 'year'}
              onCheckedChange={(checked) => handleBillingIntervalChange(checked ? 'year' : 'month')}
            />
            <Label htmlFor="billing-toggle" className={billingInterval === 'year' ? 'font-medium' : 'text-muted-foreground'}>
              Annual
            </Label>
            <span className={`text-xs font-medium min-w-[60px] ${billingInterval === 'year' ? 'text-green-600' : 'text-transparent'}`}>
              Save ~17%
            </span>
          </div>

          <div className={`grid grid-cols-1 gap-3 sm:gap-4 mt-3 sm:mt-4 ${Object.keys(data.plan_variations).length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
            {Object.keys(data.plan_variations).map((tierKey) => {
              const tierData = data.plan_variations[tierKey]
              const selectedVariation = getSelectedVariation(tierKey)
              const isCurrentTier = hasActiveSubscription && data.subscription?.plan_name === tierKey
              const isCurrentVariation = hasActiveSubscription && data.subscription?.stripe_price_id === selectedVariations[tierKey]

              return (
                <div
                  key={tierKey}
                  className={`rounded-lg p-3 sm:p-4 flex flex-col transition-all border ${
                    isCurrentTier
                      ? 'bg-primary/10 ring-2 ring-primary border-primary'
                      : 'bg-muted/50 hover:bg-muted/70 border-border'
                  }`}
                >
                  <div className="space-y-2.5 sm:space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base sm:text-lg font-semibold">{tierData.name}</h3>
                      {isCurrentTier && (
                        <span className="text-xs font-medium text-primary">Current</span>
                      )}
                    </div>

                    {/* Plan Description */}
                    <p className="text-xs text-muted-foreground">{tierData.description}</p>

                    {/* Variation Selector */}
                    <Select
                      value={selectedVariations[tierKey]}
                      onValueChange={(value) => handleVariationChange(tierKey, value)}
                    >
                      <SelectTrigger className="w-full h-9 text-sm bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getVariationsForInterval(tierKey).map((variation) => (
                          <SelectItem key={variation.stripe_price_id} value={variation.stripe_price_id}>
                            {variation.display_name}
                            {hasActiveSubscription && data.subscription?.stripe_price_id === variation.stripe_price_id && ' (Current)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div>
                      {billingInterval === 'month' ? (
                        <>
                          <span className="text-xl sm:text-2xl font-bold">${selectedVariation?.monthly_price || 0}</span>
                          <span className="text-muted-foreground text-sm">/mo</span>
                        </>
                      ) : (
                        <>
                          <span className="text-xl sm:text-2xl font-bold">${selectedVariation?.price?.toLocaleString() || 0}</span>
                          <span className="text-muted-foreground text-sm">/year</span>
                        </>
                      )}
                    </div>

                    {/* Features List */}
                    <div className="space-y-1.5 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <CheckIcon className="h-3 w-3 text-primary flex-shrink-0" />
                        <span>{selectedVariation?.credits || 0} AI credits/month</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CheckIcon className="h-3 w-3 text-primary flex-shrink-0" />
                        <span>Purchase additional credits</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CheckIcon className="h-3 w-3 text-primary flex-shrink-0" />
                        <span>Private hosting</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CheckIcon className="h-3 w-3 text-primary flex-shrink-0" />
                        <span>Full access to all features</span>
                      </div>
                    </div>

                    <Button
                      onClick={() => isFreePlan ? handleSubscribe(tierKey) : handleChangePlan(tierKey)}
                      className="w-full"
                      size="sm"
                      variant={isCurrentVariation ? 'outline' : 'default'}
                      disabled={!!hasActiveSubscription && !!isCurrentVariation}
                    >
                      {isCurrentVariation ? 'Current Plan' : 'Select'}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="w-[90vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">Change Your Plan?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              Changes will take effect immediately and your bill will be prorated. Your new plan will start right away with the selected credit amount.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              className="w-full sm:w-auto m-0"
              onClick={() => {
                setShowConfirmDialog(false)
                setPendingTierChange(null)
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-auto"
              onClick={confirmChangePlan}
            >
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
